/*
 * 微信支付统一工具：绑定预检 + 调起支付兜底 + 绑定弹窗注册器 + 扫码付款前置校验。
 *
 * 背景（2026-10-06 线上问题）：
 *   未绑定微信（users.open_id 为空）的账号在微信内调 /yljx/public/h5pay 会被后端
 *   前置拒绝——返回的 payArray 是空串。前端若不校验就交给 WeixinJSBridge，
 *   微信会弹原生错误「调用支付JSAPI缺少参数:appId」，用户看到的是一句技术报错。
 *
 * 这里把口径统一收口到四件事：
 *   1. 支付前预检（ensureWechatBound）：未绑定微信 → 直接给绑定引导，不发支付请求；
 *   2. 调起支付兜底（invokeWechatPay）：参数为空绝不调微信，改展示后端 msg 或绑定引导；
 *   3. 绑定弹窗注册器：弹窗在 App.vue 全局挂载一次，任意页面/组件都能唤起；
 *   4. 扫码付款前置校验（ensureScanPayBound / startScanPay，2026-10-09 新增）：
 *      扫码付款前先确认账号已绑定微信，未绑定则引导绑定，绑定成功后就地续跑，
 *      不整页刷新；付款码生成失败统一兜底。
 */
import { MessageBox, Message } from 'element-ui'
import apiReq from '@t/api'

/** 绑定弹窗实例（App.vue 挂载）注册进来的唤起函数；未挂载时降级为文字提示 */
let bindDialogOpener = null

export function registerWechatBindDialog (opener) {
  bindDialogOpener = typeof opener === 'function' ? opener : null
}

/**
 * 唤起微信绑定弹窗。
 * @param {Function} [onBound] 绑定成功后的续跑回调（扫码付款场景传入，用于就地继续生成付款码）
 */
export function openWechatBindDialog (onBound) {
  if (bindDialogOpener) {
    return bindDialogOpener(onBound)
  }
  Message.warning('请先在微信中完成账号绑定后再支付')
  return null
}

/** 读取本地登录态（支付必须带用户 id） */
export function readCurrentUser () {
  try {
    const raw = localStorage.getItem('user')
    return raw ? JSON.parse(raw) : null
  } catch (error) {
    return null
  }
}

export function isWechatBrowser () {
  return typeof navigator !== 'undefined' && /MicroMessenger/i.test(navigator.userAgent)
}

/**
 * 把最新登录态同步回调用方组件的 user 字段（默认 `vm.user`）。
 *
 * 背景：完成微信绑定会「把手机号挂到微信账号上 + 停用原纯手机号账号」，
 * 因此 **users.id 会变化**。续跑时必须用新的 id 下单，否则订单会记到已停用的旧账号上。
 * 各业务页的 `this.user` 是 created 时从 localStorage 取的一次性快照，不会自动更新，
 * 所以续跑前需要显式同步一次。
 *
 * @param {Object} vm    调用方组件实例
 * @param {string} [field='user'] 组件上保存登录态的字段名
 * @returns {Object|null} 最新的登录态
 */
export function syncLatestUserTo (vm, field = 'user') {
  const latest = readCurrentUser()
  if (latest && vm) {
    vm[field] = latest
  }
  return latest
}

/**
 * 该账号是否已绑定微信。
 * 后端 User 实体带 open_id 字段（@JsonProperty("open_id")），为空即未绑定，
 * 因此前端不需要额外接口就能判断。
 */
export function hasWechatBound (user = readCurrentUser()) {
  return !!(user && user.open_id)
}

/**
 * 支付前预检（微信内 JSAPI 分支用）。
 * @returns {boolean} true = 可以继续发起支付；false = 已给出提示/绑定引导，调用方应直接 return
 */
export function ensureWechatBound () {
  const user = readCurrentUser()
  if (!user || user.id === undefined || user.id === null) {
    Message.error('请先登录账号后再支付')
    return false
  }
  if (hasWechatBound(user)) {
    return true
  }
  openWechatBindDialog()
  return false
}

/**
 * 调起微信 JSAPI 支付的唯一入口（替代各页面的 WeixinJSBridge.invoke 裸调用）。
 *
 * @param {Object} request  /yljx/public/h5pay 的响应
 * @param {Function} [callback] 微信支付结果回调
 * @returns {boolean} 是否真的调起了微信支付
 */
export function invokeWechatPay (request, callback) {
  const payArray = request && request.payArray
  // 参数不是对象 = 后端已拒绝（未绑定微信 / 下单失败），绝不能交给微信
  if (!payArray || typeof payArray !== 'object') {
    if (!hasWechatBound()) {
      openWechatBindDialog()
    } else {
      Message.error((request && request.msg) || '支付发起失败，请稍后重试')
    }
    return false
  }
  if (typeof window === 'undefined' || !window.WeixinJSBridge) {
    Message.error('未检测到微信支付环境，请在微信中打开后再试')
    return false
  }
  window.WeixinJSBridge.invoke('getBrandWCPayRequest', payArray, callback || function () {})
  return true
}

/* ------------------------------------------------------------------ *
 * 扫码付款前置校验（会员升级 / 开通功能 / 日课 / 私人定制 共用）
 *
 * 口径（2026-10-09 确认）：
 *   1. 扫码付款前先确认账号是否已绑定微信；未绑定不生成付款码；
 *   2. 未绑定 → 说明弹窗 →「立即绑定微信」打开绑定弹窗，绑定成功后
 *      onBound 续跑（就地继续生成付款码，不整页刷新）；
 *   3. 「暂不绑定」→ 取消提示，本次不付款；
 *   4. 付款码生成失败统一兜底提示（原各入口在 url 为空时会弹出空白二维码）。
 *
 * 说明：绑定判定读取登录态里的 open_id（与微信内 JSAPI 分支同一口径），
 * 为本地瞬时判定，因此不需要「校验中」的 loading 态。
 * ------------------------------------------------------------------ */

/** 扫码付款共用文案（集中一处，便于统一维护） */
export const SCAN_PAY_TEXT = {
  loginRequired: '请先登录账号后再支付',
  bindTitle: '请先绑定微信',
  bindBodyWithTel: maskedTel => `检测到您的账号（手机号 ${maskedTel}）尚未绑定微信，暂时无法使用微信扫码付款。`,
  bindBodyWithoutTel: '检测到您的账号尚未绑定微信，暂时无法使用微信扫码付款。',
  bindBodySecond: '绑定微信后，付款才能准确记入本账号，会员权益即时到账。',
  bindConfirm: '立即绑定微信',
  bindCancel: '暂不绑定',
  bindCancelTip: '已取消。绑定微信后再扫码付款，权益才能准确到账。',
  boundOk: '您的账号已绑定微信，可继续扫码付款。',
  scanTip: '请使用本人微信扫描上方二维码支付，付款将记入本账号',
  payMethodLabel: '支付方式：微信支付',
  cancelPay: '取消支付',
  cancelConfirm: '您确定要取消支付吗？若点击确认无法找回本订单记录，请谨慎选择\n\n请确认！',
  bindSuccessResume: '微信绑定成功，正在为您生成付款码…',
  bindSuccess: '微信绑定成功，请重新发起支付',
  bindGiveUp: '已取消绑定。绑定微信后才能扫码付款。',
  qrFailed: '二维码生成失败，请稍后重试'
}

/**
 * 手机号脱敏：保留前 3 后 4（13812345678 → 138****5678）。
 * 位数不足 7 位时返回空串，由调用方走「不带手机号」的文案。
 */
export function maskTel (tel) {
  const digits = `${tel || ''}`.replace(/\D/g, '')
  if (digits.length < 7) {
    return ''
  }
  return `${digits.slice(0, 3)}****${digits.slice(-4)}`
}

/**
 * 扫码付款前的账号校验（所有扫码入口共用）。
 *
 * @param {Function} [onBound] 绑定成功后的续跑回调（重新执行一次扫码付款流程）
 * @returns {boolean} true = 已绑定，调用方继续生成付款码；false = 已给出提示/引导，调用方直接 return
 */
export function ensureScanPayBound (onBound) {
  const user = readCurrentUser()
  if (!user || user.id === undefined || user.id === null) {
    Message.error(SCAN_PAY_TEXT.loginRequired)
    return false
  }
  if (hasWechatBound(user)) {
    return true
  }
  const maskedTel = maskTel(user.tel)
  const html = [
    `<p>${maskedTel ? SCAN_PAY_TEXT.bindBodyWithTel(maskedTel) : SCAN_PAY_TEXT.bindBodyWithoutTel}</p>`,
    `<p>${SCAN_PAY_TEXT.bindBodySecond}</p>`
  ].join('')
  MessageBox.confirm(html, SCAN_PAY_TEXT.bindTitle, {
    dangerouslyUseHTMLString: true,
    confirmButtonText: SCAN_PAY_TEXT.bindConfirm,
    cancelButtonText: SCAN_PAY_TEXT.bindCancel,
    type: 'warning'
  }).then(() => {
    openWechatBindDialog(onBound)
  }).catch(() => {
    Message.warning(SCAN_PAY_TEXT.bindCancelTip)
  })
  return false
}

/**
 * 付款码兜底：url 为空（后端下单失败）时提示并返回空串，
 * 避免各入口弹出 src 为空的空白二维码。
 */
export function resolveScanPayUrl (request) {
  const url = request && request.url
  if (!url || typeof url !== 'string') {
    Message.error((request && request.msg) || SCAN_PAY_TEXT.qrFailed)
    return ''
  }
  return url
}

/**
 * 扫码付款弹窗（统一 C 组文案：已绑定提示 + 服务信息 + 付款码 + 扫码提示）。
 *
 * @param {Object} options
 * @param {string} options.title        弹窗标题（各入口原值，如「会员购买」「解锁优质日课」）
 * @param {string} options.serviceName  服务名称（各入口原值）
 * @param {number|string} options.price 服务金额
 * @param {string} options.qrUrl        付款码地址
 * @param {Array<string>} [options.extraLines] 各入口额外的提示行（原样 HTML）
 * @param {string} [options.imgStyle]   付款码图片样式
 * @param {Function} [options.onCancel] 用户确认取消支付后的回调（通常用于清轮询定时器）
 */
export function showScanPayQr ({ title, serviceName, price, qrUrl, extraLines = [], imgStyle = 'display:block;width:60%;margin:0 auto;', onCancel }) {
  const html = [
    `<p>${SCAN_PAY_TEXT.boundOk}</p>`,
    `<p>服务名称：${serviceName}</p>`,
    `<p>服务金额：￥${price}元</p>`,
    `<p>${SCAN_PAY_TEXT.payMethodLabel}</p>`,
    `<img style="${imgStyle}" src="${qrUrl}" />`,
    `<p style="color:#f00">${SCAN_PAY_TEXT.scanTip}</p>`,
    ...extraLines
  ].join('')
  MessageBox.alert(html, title, {
    dangerouslyUseHTMLString: true,
    confirmButtonText: SCAN_PAY_TEXT.cancelPay,
    beforeClose: (action, instance, done) => {
      if (confirm(SCAN_PAY_TEXT.cancelConfirm) === true) {
        done()
        if (typeof onCancel === 'function') {
          onCancel()
        }
      }
    }
  })
}

/**
 * 扫码付款统一入口：绑定校验 + 续跑 + 下单 + 付款码兜底 + 弹窗，全部在此收口。
 *
 * 调用方只需提供下单参数、展示文案，以及「下单成功后」的收尾（设置轮询 / 关弹窗）。
 *
 * @param {Object} options
 * @param {Object|Function} options.params getPay 的下单参数；
 *        传入函数时在**每次下单前**求值 —— 绑定会切换 users.id，
 *        续跑必须取到最新的用户 id，因此推荐传函数。
 * @param {string} options.title        付款码弹窗标题（各入口原值）
 * @param {string} options.serviceName  服务名称（各入口原值）
 * @param {number|string} options.price 服务金额
 * @param {Array<string>} [options.extraLines] 各入口额外的提示行（原样 HTML）
 * @param {string} [options.imgStyle]   付款码图片样式
 * @param {Function} options.onOrdered  (request) => void，下单成功后由调用方设置 lunxunid / 轮询 / 关弹窗
 * @param {Function} [options.onCancel] 用户确认取消支付后的回调
 * @returns {Promise<boolean>} 是否已完成下单并弹出付款码
 */
export async function startScanPay (options) {
  const {
    params, title, serviceName, price,
    extraLines = [], imgStyle, onOrdered, onCancel
  } = options

  const run = async () => {
    // params 支持惰性求值：续跑发生在绑定之后，届时 users.id 已变化，必须重新取
    const resolvedParams = typeof params === 'function' ? params() : params
    const request = await apiReq.getPay(resolvedParams)
    const qrUrl = resolveScanPayUrl(request)
    if (!qrUrl) {
      if (typeof onCancel === 'function') {
        onCancel()
      }
      return false
    }
    if (typeof onOrdered === 'function') {
      onOrdered(request)
    }
    showScanPayQr({ title, serviceName, price, qrUrl, extraLines, imgStyle, onCancel })
    return true
  }

  // 未绑定微信：给出引导并返回；用户完成绑定后由 onBound 续跑 run()
  if (!ensureScanPayBound(run)) {
    return false
  }
  return run()
}
